/**
 * Connectivity campaign run detail (#283).
 *
 * The same panels the device Connectivity tab shows, scoped to one campaign run instead of
 * one modem — which is the whole point of campaigns: "what happened during Tuesday's pass
 * campaign?" stops being a hand-built time range over a device tab.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Badge, Button,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui';
import { ResponsiveTable } from '@/components/ui/responsive-table';
import { RefreshCw, StopCircle, AlertTriangle } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import { useDateFormat } from '@/hooks/useDateFormat';
import {
  getCampaign, getCampaignEvents, getCampaignScans, getCampaignTests, getCampaignWindows,
  stopCampaign, type CampaignSummary,
} from '@/services/campaignService';

interface Props {
  runId: string;
  onStopped?: () => void;
}

const STATE_TONE: Record<string, 'default' | 'secondary' | 'destructive'> = {
  attached: 'default', hunt_attached: 'default', hunt_terrestrial: 'default',
  down: 'destructive', anchor_mismatch: 'destructive', modem_lost: 'destructive',
};

export const CampaignRunDetail: React.FC<Props> = ({ runId, onStopped }) => {
  const fmt = useDateFormat();
  const { success, error: showError } = useToast();
  const [summary, setSummary] = useState<CampaignSummary | null>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [scans, setScans] = useState<any[]>([]);
  const [tests, setTests] = useState<any[]>([]);
  const [windows, setWindows] = useState<any[]>([]);
  const [modemFilter, setModemFilter] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [stopping, setStopping] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // One failing panel must not blank the rest.
      const [s, e, sc, t, w] = await Promise.allSettled([
        getCampaign(runId),
        getCampaignEvents(runId, { limit: 300, modemImei: modemFilter === 'all' ? undefined : modemFilter }),
        getCampaignScans(runId, { limit: 200 }),
        getCampaignTests(runId, { limit: 100 }),
        getCampaignWindows(runId, { limit: 200 }),
      ]);
      if (s.status === 'fulfilled') setSummary(s.value);
      if (e.status === 'fulfilled') setEvents(e.value.items || []);
      if (sc.status === 'fulfilled') setScans(sc.value.items || []);
      if (t.status === 'fulfilled') setTests(t.value.items || []);
      if (w.status === 'fulfilled') setWindows(w.value.items || []);
    } finally {
      setLoading(false);
    }
  }, [runId, modemFilter]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);

  const onStop = async () => {
    setStopping(true);
    try {
      const r = await stopCampaign(runId);
      success(`Campaign stopped — ${r.released} modem(s) released${r.failed ? `, ${r.failed} failed` : ''}`);
      await load();
      onStopped?.();
    } catch (err: any) {
      showError(err?.message || 'Could not stop the campaign');
    } finally {
      setStopping(false);
    }
  };

  if (!summary) {
    return <Card padding="lg"><p className="text-body text-muted-foreground">Loading campaign…</p></Card>;
  }

  const running = summary.status === 'running' || summary.status === 'initializing';
  const t = summary.totals || {};
  const isHunt = summary.mode === 'hunt';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Badge variant={running ? 'default' : 'secondary'}>
          {summary.stopping ? 'stopping' : summary.status}
        </Badge>
        <span className="text-body font-medium text-foreground">{summary.name || 'Campaign'}</span>
        <Badge variant="outline">{summary.mode === 'hunt' ? 'PLMN hunt' : 'Persistent attach'}</Badge>
        {summary.schedule?.kind && <Badge variant="outline">schedule: {summary.schedule.kind}</Badge>}
        <Badge variant="secondary">no tokens</Badge>
        {summary.startedAt && (
          <span className="text-caption text-muted-foreground">started {fmt.formatDateTime(summary.startedAt)}</span>
        )}
        {summary.endsAt && running && (
          <span className="text-caption text-muted-foreground">ends {fmt.formatDateTime(summary.endsAt)}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className="mr-1 h-3.5 w-3.5" />Refresh
          </Button>
          {running && (
            <Button variant="destructive" size="sm" onClick={onStop} disabled={stopping}>
              <StopCircle className="mr-1 h-3.5 w-3.5" />{stopping ? 'Stopping…' : 'Stop'}
            </Button>
          )}
        </div>
      </div>

      {summary.windowHorizonWarning && running && (
        <Card padding="sm" className="border-accent-amber">
          <div className="flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-accent-amber mt-0.5" />
            <p className="text-body text-foreground">{summary.windowHorizonWarning.message}</p>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
        {([
          ['Attach attempts', t.attempts ?? 0],
          ['Attached', t.attaches ?? 0],
          ['Failures', t.failures ?? 0],
          ['Detaches', t.detaches ?? 0],
          isHunt ? ['Scans', t.scans ?? 0] : ['Probes', t.probes ?? 0],
          ['Tests fired', `${t.onAttachPassed ?? 0}/${t.onAttachRuns ?? 0}`],
        ] as Array<[string, string | number]>).map(([label, value]) => (
          <Card key={label} padding="sm">
            <div className="text-caption uppercase text-muted-foreground">{label}</div>
            <div className="text-h2 font-bold text-foreground">{value}</div>
          </Card>
        ))}
      </div>

      <Card padding="sm">
        <div className="text-caption uppercase text-muted-foreground mb-2">Modems ({summary.modems.length})</div>
        <ResponsiveTable
          data={summary.modems}
          keyExtractor={(m: any) => `${m.deviceId}/${m.modemImei}`}
          columns={[
            { key: 'modemImei', header: 'Modem', render: (v: any, m: any) => (
              <span className="font-mono text-caption">{m.modemModelName ? `${m.modemModelName} · ` : ''}…{String(v).slice(-6)}</span>
            ) },
            { key: 'state', header: 'Campaign', render: (v: any) => (
              <Badge variant={v === 'active' ? 'default' : v === 'refused' ? 'destructive' : 'secondary'}>{v}</Badge>
            ) },
            { key: 'connectivityStatus', header: 'Live state', render: (v: any) => (
              v?.state ? <Badge variant={STATE_TONE[v.state] || 'secondary'}>{v.state}</Badge> : <span>—</span>
            ) },
            { key: 'connectivityStatus', header: 'PLMN / RAT', render: (v: any) => (
              <span>{v?.plmn ? `${v.plmn}${v.rat ? ` · ${v.rat}` : ''}` : '—'}</span>
            ) },
            { key: 'stillManaged', header: 'Owned', render: (v: any, m: any) => (
              m.state === 'active' && !v
                ? <Badge variant="destructive">drifted</Badge>
                : <span className="text-caption text-muted-foreground">{v ? 'yes' : '—'}</span>
            ) },
            { key: 'refusedCode', header: 'Note', render: (v: any) => <span className="text-caption text-muted-foreground">{v || '—'}</span> },
          ] as any}
        />
      </Card>

      {summary.modems.length > 1 && (
        <Select value={modemFilter} onValueChange={setModemFilter}>
          <SelectTrigger className="w-72"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All modems</SelectItem>
            {summary.modems.map((m) => (
              <SelectItem key={m.modemImei} value={m.modemImei}>…{m.modemImei.slice(-6)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {tests.length > 0 && (
        <Card padding="sm">
          <div className="text-caption uppercase text-muted-foreground mb-2">Tests fired on attach ({tests.length})</div>
          <ResponsiveTable
            data={tests}
            keyExtractor={(r: any) => r.id}
            paginated
            defaultPageSize={10}
            columns={[
              { key: 'createdAt', header: 'Time', render: (v: any) => <span className="text-caption">{fmt.formatDateTime(v)}</span> },
              { key: 'flowName', header: 'Test' },
              { key: 'status', header: 'Status', render: (v: any) => <Badge variant={v === 'completed' ? 'default' : v === 'failed' ? 'destructive' : 'secondary'}>{v}</Badge> },
              { key: 'result', header: 'Outcome', render: (v: any) => (
                <span className="text-caption text-muted-foreground">{v?.success === true ? 'passed' : v?.error || '—'}</span>
              ) },
            ] as any}
          />
        </Card>
      )}

      {isHunt && (
        <Card padding="sm">
          <div className="text-caption uppercase text-muted-foreground mb-2">
            Pass windows ({windows.length}) — {summary.schedule?.kind === 'rule' ? 'generated by the schedule' : 'uploaded'}
          </div>
          <ResponsiveTable
            data={windows}
            keyExtractor={(w: any) => w.id}
            paginated
            defaultPageSize={10}
            columns={[
              { key: 'start', header: 'Start', render: (v: any) => <span className="text-caption">{fmt.formatDateTime(v)}</span> },
              { key: 'end', header: 'End', render: (v: any) => <span className="text-caption">{fmt.formatDateTime(v)}</span> },
              { key: 'source', header: 'Source' },
              { key: 'satelliteId', header: 'Satellite', render: (v: any) => <span>{v || '—'}</span> },
            ] as any}
          />
        </Card>
      )}

      {isHunt && scans.length > 0 && (
        <Card padding="sm">
          <div className="text-caption uppercase text-muted-foreground mb-2">Scans ({scans.length}) — negatives included</div>
          <ResponsiveTable
            data={scans}
            keyExtractor={(r: any) => r.id}
            paginated
            defaultPageSize={10}
            columns={[
              { key: 'ts', header: 'Time', render: (v: any) => <span className="text-caption">{fmt.formatDateTime(v)}</span> },
              { key: 'targetSeen', header: 'Target', render: (v: any) => <Badge variant={v ? 'default' : 'secondary'}>{v ? 'seen' : 'not seen'}</Badge> },
              { key: 'cells', header: 'Cells', render: (v: any) => <span>{Array.isArray(v) ? v.length : 0}</span> },
              { key: 'durationMs', header: 'Duration', render: (v: any) => <span className="text-caption">{v ? `${Math.round(v / 1000)}s` : '—'}</span> },
            ] as any}
          />
        </Card>
      )}

      <Card padding="sm">
        <div className="text-caption uppercase text-muted-foreground mb-2">Events ({events.length})</div>
        <ResponsiveTable
          data={events}
          keyExtractor={(r: any) => r.id}
          paginated
          defaultPageSize={25}
          columns={[
            { key: 'ts', header: 'Time', render: (v: any) => <span className="text-caption">{fmt.formatDateTime(v)}</span> },
            { key: 'kind', header: 'Event', render: (v: any) => (
              <Badge variant={v === 'attach_fail' ? 'destructive' : v === 'attach_ok' || v === 'bearer_up' ? 'default' : 'secondary'}>
                {String(v).replace(/_/g, ' ')}
              </Badge>
            ) },
            { key: 'plmn', header: 'PLMN / RAT', render: (v: any, r: any) => <span>{v ? `${v}${r.rat ? ` · ${r.rat}` : ''}` : '—'}</span> },
            { key: 'cause', header: 'Cause', render: (v: any) => <span className="text-caption">{v ? (v.text || `${v.code}`) : '—'}</span> },
            { key: 'modemImei', header: 'Modem', render: (v: any) => <span className="font-mono text-caption">…{String(v || '').slice(-6)}</span> },
          ] as any}
        />
      </Card>
    </div>
  );
};

export default CampaignRunDetail;
